import React, { useState, useEffect } from 'react'
import { Badge, Card, Col, Row, Spinner, Stack, Modal, Button, Alert } from 'react-bootstrap';
import { useLocation, useOutletContext } from 'react-router';
import moment from 'moment';
import 'moment/locale/nl';
import Breadcrumbs from '../Components/Breadcrumbs';
import { Link } from 'react-router-dom';
import AcademyLoading from '../Components/AcademyLoading';
import { functions, analytics } from '../utils/firebase';
import { httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';

export default function Series(props) {
    const {routes, user, setUser, series, setSeries, purchases} = useOutletContext();
    const seriesId = new URLSearchParams(useLocation().search).get('sid')
    const [selectedSeries, setSelectedSeries] = useState(null)
    const [access, setAccess] = useState(false)
    const [showSignupModal, setShowSignupModal] = useState(false)
    const [signupForm, setSignupForm] = useState({email:'', optin:true, locatie:'Series'})
    const [submittingForm, setSubmittingForm] = useState(false)
    const [formError, setFormError] = useState('')

    // Storage helper functions for incognito compatibility
    const getOptinEmail = () => {
        // Try sessionStorage first (works in incognito), then localStorage
        return sessionStorage.getItem('ukies-webinars-optin-email') || 
               localStorage.getItem('ukies-webinars-optin-email');
    };

    const setOptinEmail = (email) => {
        // Always use sessionStorage (works in incognito)
        sessionStorage.setItem('ukies-webinars-optin-email', email);
        
        // Try localStorage as backup (for non-incognito persistence)
        try {
            localStorage.setItem('ukies-webinars-optin-email', email);
        } catch (e) {
            // localStorage might not be available in incognito
        }
    };

    // Update selectedSeries when series data becomes available
    useEffect(() => {
        if (series && series.length > 0 && seriesId) {
            const foundSeries = series.find((s) => s.uuid === seriesId);
            if (foundSeries) {
                setSelectedSeries(foundSeries);
                setSignupForm({...signupForm, locatie: foundSeries.type + ': ' + foundSeries.name});
                
                // Track series page view
                logEvent(analytics, 'page_view', {
                    page_title: `Ukie's Academy - ${foundSeries.name}`,
                    page_location: window.location.href,
                    content_type: 'series',
                    series_id: foundSeries.uuid,
                    series_name: foundSeries.name,
                    series_type: foundSeries.type,
                    is_free: foundSeries.isFree,
                    requires_signup: foundSeries.freeRequiresSignup
                });
                
                // Check if user has already opted in this session
                const sessionOptinEmail = getOptinEmail();
                const hasOptedIn = (user && user.optedIn) || sessionOptinEmail;
                const hasAccess = (foundSeries.isFree && foundSeries.freeRequiresSignup && hasOptedIn) || 
                                (foundSeries.isFree && !foundSeries.freeRequiresSignup) || 
                                (!foundSeries.isFree && foundSeries.access);
                setAccess(hasAccess);
                
                // Track access status
                logEvent(analytics, 'series_access_check', {
                    series_id: foundSeries.uuid,
                    series_name: foundSeries.name,
                    has_access: hasAccess,
                    is_free: foundSeries.isFree,
                    requires_signup: foundSeries.freeRequiresSignup,
                    user_opted_in: hasOptedIn
                });
                
                // Show signup modal if user doesn't have access and series requires signup and hasn't opted in this session
                if (!hasAccess && foundSeries.isFree && foundSeries.freeRequiresSignup && user && user.optedIn === false && !sessionOptinEmail) {
                    setShowSignupModal(true);
                    
                    // Track signup modal display
                    logEvent(analytics, 'signup_modal_shown', {
                        series_id: foundSeries.uuid,
                        series_name: foundSeries.name,
                        trigger: 'series_access_required'
                    });
                }
            }
        }
    }, [series, seriesId, user]);
    useEffect(() => {
        if(signupForm.email === '') {
            const sessionOptinEmail = getOptinEmail();
            if(user && user.email) {
                setSignupForm({...signupForm, email:user.email});
            } else if(sessionOptinEmail) {
                setSignupForm({...signupForm, email:sessionOptinEmail});
            }
        }
    },[user])

    // Update page title when series is loaded
    useEffect(() => {
        if (selectedSeries && selectedSeries.name) {
            document.title = "Ukie's Academy - " + selectedSeries.name;
        }
        
        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = "Ukie's Academy";
        };
    }, [selectedSeries]);

    if(!selectedSeries) {
        return(
            <AcademyLoading type='Cursus' />
        ) 
    }

    const handleLinkClick = (e, contentType, contentItem) => {
        // Track click attempt
        logEvent(analytics, 'content_click', {
            content_type: contentType,
            content_id: contentItem.uuid,
            content_name: contentItem.name,
            series_id: selectedSeries.uuid,
            series_name: selectedSeries.name,
            has_access: access,
            click_allowed: access
        });

        // Don't Link if the user does not have access
        if(!access) {
            e.preventDefault();
            
            // Track access denied
            logEvent(analytics, 'content_access_denied', {
                content_type: contentType,
                content_id: contentItem.uuid,
                content_name: contentItem.name,
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                reason: selectedSeries.isFree ? 'signup_required' : 'purchase_required'
            });
            
            // Show signup modal if series requires signup and user optedIn is false and hasn't opted in this session
            const sessionOptinEmail = getOptinEmail();
            if(selectedSeries.isFree && selectedSeries.freeRequiresSignup && user && user.optedIn === false && !sessionOptinEmail) {
                setShowSignupModal(true);
                
                // Track signup modal display from content click
                logEvent(analytics, 'signup_modal_shown', {
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    trigger: 'content_click_blocked',
                    content_type: contentType,
                    content_id: contentItem.uuid
                });
            }
        }
    }

    const handleCloseModal = () => {
        // Track modal close (potential abandonment)
        logEvent(analytics, 'signup_modal_closed', {
            series_id: selectedSeries?.uuid,
            series_name: selectedSeries?.name,
            had_form_data: !!signupForm.email,
            had_error: !!formError
        });
        
        setShowSignupModal(false);
        setFormError('');
    }

    const handleOptinFormSubmit = async (e) => {
        e.preventDefault();
        
        if (!signupForm.email || !signupForm.optin) {
            setFormError('Vul alle velden in en geef toestemming voor het ontvangen van e-mails.');
            
            // Track form validation error
            logEvent(analytics, 'signup_form_error', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                error_type: 'validation_error',
                missing_email: !signupForm.email,
                missing_optin: !signupForm.optin
            });
            
            return;
        }

        setSubmittingForm(true);
        setFormError('');

        // Track signup attempt
        logEvent(analytics, 'signup_attempt', {
            series_id: selectedSeries.uuid,
            series_name: selectedSeries.name,
            email: signupForm.email
        });

        try {
            const ukiesFormEntry = httpsCallable(functions, 'ukiesFormEntry');
            const result = await ukiesFormEntry({
                form_id: 3,
                email: signupForm.email,
                locatie: 'Serie - ' + selectedSeries.name
            });

            if (result.data.status === 'success') {
                // Track successful signup
                logEvent(analytics, 'sign_up', {
                    method: 'email',
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    email: signupForm.email
                });
                
                // Update user state to set optedIn=true
                setUser(prevUser => ({
                    ...prevUser,
                    optedIn: true
                }));
                
                // Save email to sessionStorage so user isn't prompted again this session
                setOptinEmail(signupForm.email);
                
                // Close modal and allow access
                setShowSignupModal(false);
                setAccess(true);
                
                // Track access granted
                logEvent(analytics, 'access_granted', {
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    access_method: 'signup_completion'
                });
            } else {
                // Track signup failure
                logEvent(analytics, 'signup_form_error', {
                    series_id: selectedSeries.uuid,
                    series_name: selectedSeries.name,
                    error_type: 'api_error',
                    error_reason: result.data.reason || 'unknown_error'
                });
                
                setFormError(result.data.reason || 'Er is een fout opgetreden bij het aanmelden.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            
            // Track signup technical error
            logEvent(analytics, 'signup_form_error', {
                series_id: selectedSeries.uuid,
                series_name: selectedSeries.name,
                error_type: 'technical_error',
                error_message: error.message
            });
            
            setFormError('Er is een fout opgetreden bij het aanmelden. Probeer het opnieuw.');
        } finally {
            setSubmittingForm(false);
        }
    }

    return (
        <Stack>
            <div className="structure-fullwidth" style={{backgroundImage:'url('+selectedSeries.image+')',backgroundPosition:`${selectedSeries?.imagePositionX || 50}% ${selectedSeries?.imagePositionY || 50}%`}}>
                <div className="structure header-banner structure-1 lg:structure-2 items-end" style={{alignItems:'flex-end',height:'500px'}}>
                    <div className="component component-text white text-shadow">
                        <div className="preheader-text white">Ukie's academy</div>
                        <div className="header-text">{selectedSeries.name}</div>
                    </div>
                </div>
            </div>
            <Breadcrumbs routes={routes} user={user} series={series}/>
            <Row style={{padding:'2rem 0'}}>
                <div className='component component-text'>
                    <div className='header-text-small'>
                        {selectedSeries.name}
                    </div>
                    <div className='body-text' dangerouslySetInnerHTML={{__html:selectedSeries.desciption}}>
                    </div>
                </div>
            </Row>
            <div>
                {
                    selectedSeries.modules && selectedSeries.modules.length > 0 &&
                    <>
                        <Row>
                            <div className='component component-text'>
                                <div className='header-text-alt' style={{fontSize:'1.5rem'}}>
                                    Modules:
                                </div>
                            </div>
                        </Row>
                        <Row gap={3} style={{gap:'2rem 0rem',paddingTop:'0rem'}}>
                            { selectedSeries.modules.map((m, idx) => {
                                return(
                                    <Col xs={12} md={6} lg={4} xl={3} key={m.uuid} className='academy-col'>
                                        <Link
                                            to={access ? '/series/video?vid=' + m.uuid : '/series?sid=' + selectedSeries.uuid}
                                            onClick={(e) => handleLinkClick(e, 'module', m)}
                                        >
                                            <Card className={access ? 'academy-card' : 'academy-card card-inactive'}>
                                                <div style={{position:'relative'}}>
                                                    <div className='thumbnail-container' style={{overflow:'hidden',borderTopLeftRadius:'calc(0.375rem - 1px)',borderTopRightRadius:'calc(0.375rem - 1px)'}}>
                                                        <Card.Img className='thumbnail' variant="top" src={m.thumbnail}/>
                                                    </div>
                                                    <Card.Img className='play-button' variant="top" src='/images/play-button-2.png'/>
                                                </div>
                                                <Card.Body>
                                                    <div style={{display:'none', gap:'0.5rem',paddingBottom:'0.5rem'}}>
                                                        { m.tags && m.tags.length > 0 && m.tags.map((tag, idx) => {
                                                            return(<Badge key={idx} style={{backgroundColor:tag.color === 'normal' ? 'var(--ukies-light-green)' : 'var(--ukies-dark-orange)', fontSize:'0.75rem', fontWeight:'normal'}} bg="custom">{tag.label}</Badge>)
                                                        })}
                                                    </div>
                                                    <div className="component-text">
                                                        <Card.Title className="header-text-small" style={{fontSize:'1.6rem',lineHeight:'1.6rem',padding:'0 0 0.75rem 0'}}>{m.name}</Card.Title>                                                    
                                                        <Card.Text className="body-text-small justify" style={{fontSize:'0.8rem'}}>{m.shortDesc}</Card.Text>
                                                    </div>
                                                    <Card.Text as="div" className="body-text-small" style={{fontSize:'0.75rem',textAlign:'right',fontStyle:'italic',marginBottom:'0.75rem',display:'flex',flexDirection:'row',justifyContent:'space-between'}}>
                                                        <div></div>
                                                        <div>
                                                            {moment.utc(m.length*1000).format('H') > 0 && moment.utc(m.length*1000).format('H') + 'u'} {moment.utc(m.length*1000).format('m')}m {moment.utc(m.length*1000).format('s')}s
                                                        </div>
                                                    </Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </Link>
                                    </Col>
                                )
                            }) }
                        </Row>
                    </>
                }
                {
                    selectedSeries.files && selectedSeries.files.length > 0 &&
                    <>
                        <Row style={{paddingTop:'2rem'}}>
                            <div className='component component-text'>
                                <div className='header-text-alt' style={{fontSize:'1.5rem'}}>
                                    Bestanden:
                                </div>
                            </div>
                        </Row>
                        <Row gap={3} style={{gap:'2rem 0rem',paddingTop:'0rem'}}>
                            { selectedSeries.files.map((f, idx) => {
                                return(
                                    <Col xs={12} md={6} lg={4} xl={3} key={f.uuid} className='academy-col'>
                                        <Link
                                            to={access ? '/series/file?fid=' + f.uuid : '/series?sid=' + selectedSeries.uuid}
                                            onClick={(e) => handleLinkClick(e, 'file', f)}
                                        >
                                            <Card className={access ? 'academy-card' : 'academy-card card-inactive'}>
                                                <div style={{position:'relative'}}>
                                                    <div className='thumbnail-container' style={{overflow:'hidden',borderTopLeftRadius:'calc(0.375rem - 1px)',borderTopRightRadius:'calc(0.375rem - 1px)'}}>
                                                        <Card.Img className='thumbnail' variant="top" src={f.thumbnail}/>
                                                    </div>
                                                </div>
                                                <Card.Body>
                                                    <div style={{display:'none', gap:'0.5rem',paddingBottom:'0.5rem'}}>
                                                        { f.tags && f.tags.length > 0 && f.tags.map((tag, idx) => {
                                                            return(<Badge key={idx} style={{backgroundColor:tag.color === 'normal' ? 'var(--ukies-light-green)' : 'var(--ukies-dark-orange)', fontSize:'0.75rem', fontWeight:'normal'}} bg="custom">{tag.label}</Badge>)
                                                        })}
                                                    </div>
                                                    <div className="component-text">
                                                        <Card.Title className="header-text-small" style={{fontSize:'1.6rem',lineHeight:'1.6rem',padding:'0 0 0.75rem 0'}}>{f.name}</Card.Title>                                                    
                                                        <Card.Text className="body-text-small justify" style={{fontSize:'0.8rem'}}>{f.shortDesc}</Card.Text>
                                                    </div>
                                                    <Card.Text as="div" className="body-text-small" style={{fontSize:'0.75rem',textAlign:'right',fontStyle:'italic',marginBottom:'0.75rem',display:'flex',flexDirection:'row',justifyContent:'space-between'}}>
                                                        <div></div>
                                                        <div>
                                                            {f.fileType} {f.type}
                                                        </div>
                                                    </Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </Link>
                                    </Col>
                                )
                            }) }
                        </Row>
                    </>
                }
            </div>

            {/* Newsletter Signup Modal */}
            <Modal 
                show={showSignupModal} 
                onHide={handleCloseModal} 
                backdrop={true} 
                keyboard={true}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Schrijf in voor gratis toegang</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleOptinFormSubmit}>
                    <Modal.Body>
                        <div className="component component-text" style={{fontSize:'0.9rem'}}>
                            Wil je graag toegang tot alle gratis cursussen en masterclasses op Ukie's Academy? <br /><br />Met deze inschijving krijg je direct toegang tot alle gratis content op Ukie's Academy.
                        </div>

                        {formError && (
                            <Alert variant="danger" className="mt-3">
                                {formError}
                            </Alert>
                        )}

                        <div className="grid grid-cols-12 gap-y-1 gap-x-4 mt-4">
                            <div className="col-span-12">
                                <div className="eui-label" style={{paddingBottom:'0'}}>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-mail</label>
                                </div>
                                <div className="mt-1 relative">
                                    <input 
                                        id="email" 
                                        name="email" 
                                        className="eui-input-wrapper" 
                                        type="email" 
                                        value={signupForm.email} 
                                        onChange={(e) => setSignupForm({...signupForm, email:e.target.value})} 
                                        autoComplete="off" 
                                        required 
                                        disabled={submittingForm}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:'0.375rem',padding:'1rem 0 0 0'}}>
                            <input 
                                type="checkbox" 
                                checked={signupForm.optin}
                                onChange={(e) => setSignupForm({...signupForm, optin:e.target.checked})}
                                disabled={submittingForm}
                                required
                            />
                            <label>Ik geef toestemming mij e-mails te sturen</label>
                        </div>
                        <div style={{fontSize:'0.8rem',padding:'0.5rem 0 0 0'}}>
                            <i>(Geen zorgen, ik zal je niet spammen met e-mails.)</i>
                        </div>
                        <div className="component component-button optin-button" >
                            <Button 
                                type="submit" 
                                className='highlight' 
                                style={{border:'none'}} 
                                disabled={submittingForm || !signupForm.optin}
                            >
                                {submittingForm ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Bezig met aanmelden...
                                    </>
                                ) : (
                                    `Bekijk deze ${selectedSeries.type.toLowerCase()} gratis`
                                )}
                            </Button>
                        </div>
                    </Modal.Body>
                </form>
            </Modal>
        </Stack>
    );
}